import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpenIcon,
  ChatBubbleBottomCenterIcon,
  CheckIcon,
  XCircleIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useContext } from "react";
import { AuthContext } from "../AuthContext";
import { EyeIcon } from "@heroicons/react/20/solid";
export default function MeetingAlert() {
  const navigate = useNavigate();
  const [reply, setReply] = useState(null);
  const [bidHours, setBidHours] = useState("");
  const [messages, setMessages] = useState([]);
  const [instructionText, setInstructionText] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const MAX_MESSAGE_LENGTH = 500;

  const user = useContext(AuthContext);

  // Error boundary to prevent component crashes
  useEffect(() => {
    const handleError = (error) => {
      console.error('MessageAlert component error:', error);
      setHasError(true);
      // Reset error state after a delay
      setTimeout(() => setHasError(false), 5000);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  // If there's an error, render nothing to prevent blank screens
  if (hasError) {
    return null;
  }

 

  // Mobile detection helper
  const isMobile = () => {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  // Initialize audio context and load notification sound (mobile-safe)
  useEffect(() => {
    // Skip all audio initialization on mobile devices to prevent issues
    if (isMobile()) {
      console.log('Mobile device detected - audio disabled for compatibility');
      return;
    }

    // Initialize notification sound for desktop browsers only
    const initializeSound = async () => {
      try {
        // Only initialize audio on desktop browsers
        if ('Audio' in window && !isMobile()) {
          notificationSoundRef.current = new Audio('/pop.mp3');
          notificationSoundRef.current.preload = 'auto';
          
          // Set volume to a reasonable level
          notificationSoundRef.current.volume = 0.5;
        }

        // Try AudioContext only on desktop
        if (!isMobile() && (window.AudioContext || window.webkitAudioContext)) {
          try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContextClass();
            
            // Handle suspended state
            if (audioContextRef.current.state === 'suspended') {
              const resumeAudio = () => {
                if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                  audioContextRef.current.resume().catch(console.error);
                }
              };
              document.addEventListener('click', resumeAudio, { once: true });
              document.addEventListener('touchstart', resumeAudio, { once: true });
            }
          } catch (audioContextError) {
            console.warn('AudioContext initialization failed:', audioContextError);
          }
        }
      } catch (error) {
        console.warn('Audio initialization failed, running in silent mode:', error);
        // Continue without throwing
      }
    };

    initializeSound();
    
    return () => {
      // Clean up audio context when component unmounts
      try {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch(console.error);
        }
      } catch (error) {
        console.warn('Error cleaning up audio context:', error);
      }
    };
  }, []);

  useEffect(() => {
    let ws;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;
    const reconnectDelay = 3000;
    let isConnecting = false;
    
    const connectWebSocket = () => {
      if (isConnecting) return;
      isConnecting = true;
      
      try {
        ws = new WebSocket("wss://api.insightos.co.uk/ws");
        
        ws.onopen = () => {
          console.log("✅ WebSocket connected");
          reconnectAttempts = 0;
          isConnecting = false;
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "ping") return;
            
            if (data.success && Array.isArray(data.data)) {
              setMessages(prev => {
                // Prevent duplicate messages
                const newMessages = data.data.filter(newMsg => 
                  !prev.some(existingMsg => existingMsg.id === newMsg.id)
                );
                
                // Play notification sound if there are new messages
                if (newMessages.length > 0) {
                  playNotificationSound();
                }
                
                return [...prev, ...newMessages];
              });
            }
          } catch (error) {
            console.error("Failed to process message:", error);
          }
        };
        
        ws.onclose = (event) => {
          isConnecting = false;
          if (reconnectAttempts < maxReconnectAttempts) {
            console.log(`WebSocket closed. Reconnecting (${reconnectAttempts + 1}/${maxReconnectAttempts})...`);
            reconnectAttempts++;
            setTimeout(connectWebSocket, reconnectDelay);
          } else {
            console.error("Maximum reconnect attempts reached. Please refresh the application.");
          }
        };
        
        ws.onerror = (error) => {
          console.error("❌ WebSocket error:", error);
          isConnecting = false;
        };
      } catch (error) {
        console.error("Failed to establish WebSocket connection:", error);
        isConnecting = false;
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          setTimeout(connectWebSocket, reconnectDelay);
        }
      }
    };
    
   
    
    connectWebSocket();
    
    // Add event listener to request notification permission on first user interaction (desktop only)
    if (!isMobile()) {
      document.addEventListener('click', requestNotificationPermission, { once: true });
    }
    
    return () => {
      if (ws) {
        ws.onclose = null; // Prevent auto-reconnect when intentionally closing
        ws.close();
      }
      if (!isMobile()) {
        document.removeEventListener('click', requestNotificationPermission);
      }
    };
  }, []);
  

  useEffect(() => {
    if (!reply) return;
    if (reply.overtime === 1) navigate("/overtime-manager", { state: { reply } });
    
    else if ([2, 3].includes(reply.overtime)) navigate("/chatdashboard", { state: { reply } });
  }, [reply, navigate]);

  const markAsRead = async (id) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/markread/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const acceptOvertime = async (overtimeId, id) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/acceptovertime`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ overtimeId }),
      });
      toast.success("Overtime accepted");
      markAsRead(id);
    } catch (error) {
      console.error(error);
    }
  };

  const rejectOvertime = async (id, overtimeId) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/rejectovertimeteam`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ overtimeId }),
      });
      toast("Overtime rejected");
      markAsRead(id);
    } catch (error) {
      console.error(error);
    }
  };

  // Play notification sound that works even when browser tab is not active (mobile-safe)
  const playNotificationSound = () => {
    // Skip all sound and notification on mobile devices
    if (isMobile()) {
      console.log('Mobile device - notifications disabled');
      return;
    }

    try {
      // Desktop-only notification handling
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          try {
            new Notification('New Message', {
              icon: '/2.png',
              silent: false,
              tag: 'insightos-message' // Prevent duplicate notifications
            });
          } catch (notifError) {
            console.warn('Desktop notification failed:', notifError);
          }
        }
      }
      
      // Desktop-only sound playing
      if (notificationSoundRef.current && !isMobile()) {
        const playPromise = notificationSoundRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.log("Browser blocked autoplay:", err.message);
            // Try Web Audio as fallback only on desktop
            playWithWebAudio();
          });
        }
      } else if (!isMobile()) {
        // Only use Web Audio API on desktop
        playWithWebAudio();
      }
    } catch (error) {
      console.warn("Error playing notification sound:", error);
      // Continue silently without throwing
    }
  };

  // Play sound using Web Audio API (works in background tabs) - desktop only
  const playWithWebAudio = () => {
    // Skip Web Audio API completely on mobile devices
    if (isMobile()) {
      return;
    }

    try {
      if (audioContextRef.current && audioBufferRef.current) {
        // Resume audio context if it was suspended (browser policy)
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().catch(console.error);
        }
        
        // Create a sound source
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBufferRef.current;
        source.connect(audioContextRef.current.destination);
        source.start(0);
      } else if (typeof window !== 'undefined' && window.require) {
        // Fallback for Electron (desktop app)
        try {
          const audio = new Audio('/pop.mp3');
          audio.volume = 0.5;
          audio.play().catch(console.error);
        } catch (electronError) {
          console.warn('Electron audio fallback failed:', electronError);
        }
      }
    } catch (error) {
      console.warn("Web Audio API error:", error);
      // Continue silently without throwing
    }
  };

  // Request notification permission on first user interaction (desktop only)
  const requestNotificationPermission = () => {
    // Skip notification permission requests on mobile
    if (isMobile()) {
      return;
    }

    try {
      if ('Notification' in window && 
          Notification.permission !== 'granted' && 
          Notification.permission !== 'denied') {
        Notification.requestPermission().catch(error => {
          console.warn('Notification permission request failed:', error);
        });
      }
    } catch (error) {
      console.warn('Error requesting notification permission:', error);
    }
  };

  // Legacy sound playing method for other parts of the app (mobile-safe)
  const playSound = (sound) => {
    // Skip sound playing on mobile devices
    if (isMobile()) {
      return;
    }

    try {
      const audio = new Audio(sound);
      audio.volume = 0.5;
      audio.play().catch((error) => {
        console.warn("Error playing sound:", error);
      });
    } catch (error) {
      console.warn("Error creating audio element:", error);
    }
  };

  const submitBid = async (id, overtimeId) => {
    if (!bidHours || isNaN(bidHours) || bidHours <= 0) {
      toast.error("Please enter a valid number of hours.");
      return;
    }

    try {
      await fetch(`https://api.insightos.co.uk/api/bid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ overtimeId, hours: bidHours }),
      });
      toast.success("Bid submitted");
      markAsRead(id);
    } catch (error) {
      console.error(error);
    }
  };

  const sendMessage = async (id) => {
    const cappedInstruction = String(instructionText || '').slice(0, MAX_MESSAGE_LENGTH);
    if (!cappedInstruction.trim()) {
      toast.error('Message cannot be empty.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/sendreplyalert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, content: cappedInstruction }),
      });
      const json = await response.json();
      if (json.success) {
        toast("Message sent");
        setInstructionText("");
        setReply(null);
        
        // Use regular playSound for user-initiated sounds (these work in all contexts)
        playSound("/woosh.mp3");
        
        setTimeout(() => {
          setLoading(false);
          setMessages((prev) => prev.filter((msg) => msg.id !== id));
        }, 2000);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  if (!messages.length) return null;

  const message = messages[0];
  const isOvertime = message.overtime === 2;
  const isBidOverTime = message.overtime === 3;

  console.log(message)
  // Treat as project chat only when a projectId is present/truthy
  const isProjectChat = !!message.projectId;

  const handleShow = async (message) => {
    if (user.access === "manager" || user.access === "admin") {

      // we now want to delete the message 

      try {
        await fetch(`https://api.insightos.co.uk/api/deletemessage/${message.id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        setMessages((prev) => prev.filter((msg) => msg.id !== message.id));
      } catch (error) {
        console.error(error);
      }

      navigate("/project-management", { state: { projectId: message.projectId } });
    } else if (user.access === "staff") {

      // we now want to delete the message
      try {
        await fetch(`https://api.insightos.co.uk/api/deletemessage/${message.id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        setMessages((prev) => prev.filter((msg) => msg.id !== message.id));

      } catch (error) {
        console.error(error);
      }
      navigate("/staff-project", { state: { projectId: message.projectId } });


    }

  };


  const handleDelete = async (message) => {
    if (user.access === "manager" || user.access === "admin") {
      try {
        await fetch(`https://api.insightos.co.uk/api/deletemessage/${message.id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        setMessages((prev) => prev.filter((msg) => msg.id !== message.id));
      } catch (error) {
        console.error(error);
      }
    } else if (user.access === "staff") {
      setMessages((prev) => prev.filter((msg) => msg.id !== message.id));
      navigate("/project-chat", { state: { message } });
    }
  };

  if (message.GoogleMeetUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6 pointer-events-none">
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <AnimatePresence>
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ 
                opacity: 1, 
                y: 0, 
                scale: [1, 1.02, 1] 
              }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              transition={{ 
                duration: 0.5,
                scale: {
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
              className="pointer-events-auto w-full max-w-md flex flex-col shadow-2xl rounded-xl bg-white ring-1 ring-black/5 overflow-hidden border-l-4 border-green-500"
            >
              <div className="p-5 flex items-start relative">
                <div className="flex-1 pr-10">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                    Video Call
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">Incoming call from <span className="font-semibold text-gray-900">{message.from}</span></p>
                </div>
                <img 
                  src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-96dp/logo_meet_2020q4_color_2x_web_96dp.png" 
                  alt="Google Meet" 
                  className="h-12 w-12 absolute top-4 right-4"
                />
              </div>
              <div className="flex bg-gray-50">
                <button
                  onClick={() => {
                    window.open(message.GoogleMeetUrl, '_blank');
                    markAsRead(message.id);
                  }}
                  className="flex-1 py-4 text-sm font-bold text-white bg-green-600 hover:bg-green-700 transition-colors focus:outline-none flex items-center justify-center"
                >
                  <span className="mr-2">📹</span> Join Call
                </button>
                <button
                  onClick={() => markAsRead(message.id)}
                  className="flex-1 py-4 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none border-t border-gray-200"
                >
                  Dismiss
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }


  if (!isProjectChat) {
    return (
      <div className="fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6 pointer-events-none">
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <AnimatePresence>
        <motion.div
          key={message.id}
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95, y: 50 }}
          transition={{ duration: 0.35 }}
          style={{
          // Ensure the alert doesn't go off-screen on mobile
          maxWidth: 'min(400px, calc(100vw - 2rem))',
          minHeight: 'auto'
          }}
          className="pointer-events-auto w-full max-w-md flex flex-col shadow-xl rounded-lg bg-indigo-600 bg-opacity-70 backdrop-blur-xl ring-1 ring-black/5"
        >
          <div className="flex w-full items-start p-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-white">From: {message.from}</p>
            <p className="mt-1 text-sm text-white">{message.content}</p>
            <p className="mt-1 text-sm text-white">Date: {new Date(message.createdAt).toLocaleString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit', 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
            }).replace(',', '')}</p>
          </div>
          </div>
          <div className="flex w-full divide-x divide-gray-200">
          {isOvertime ? (
            <>
            <button
              onClick={() => acceptOvertime(message.overtimeid, message.id)}
              data-component="message-alert-actions"
              data-feature="accept_overtime_put"
              className="w-full py-3 text-white hover:bg-green-500"
            >
              <CheckIcon className="h-5 w-5 inline mr-2" /> Accept
            </button>
            <button
              onClick={() => rejectOvertime(message.id, message.overtimeid)}
              data-component="message-alert-actions"
              data-feature="reject_overtime_put"
              className="w-full py-3 text-white hover:bg-red-500"
            >
              <XCircleIcon className="h-5 w-5 inline mr-2" /> Reject
            </button>
            </>
          ) : isBidOverTime ? (
            <>
            <input
              type="number"
              value={bidHours}
              onChange={(e) => setBidHours(e.target.value)}
              className="w-full px-4 py-2 border border-indigo-300 text-indigo-700 rounded"
              placeholder="Enter bid hours"
            />
            <button
              onClick={() => submitBid(message.id, message.overtimeid)}
              data-component="message-alert-actions"
              data-feature="submit_overtime_bid_post"
              className="w-full py-3 text-white hover:bg-green-500"
            >
              <CheckIcon className="h-5 w-5 inline mr-2 animate-bounce" /> Bid
            </button>
            <button
              onClick={() => rejectOvertime(message.id, message.overtimeid)}
              data-component="message-alert-actions"
              data-feature="reject_overtime_put"
              className="w-full py-3 text-white hover:bg-red-500"
            >
              <XCircleIcon className="h-5 w-5 inline mr-2" /> Reject
            </button>
            </>
          ) : (
            <>
            <button onClick={() => setReply(message)} className="w-full py-3 text-white hover:bg-indigo-500">
              <ChatBubbleBottomCenterIcon className="h-5 w-5 inline mr-2" /> Reply
            </button>
            <button
              onClick={() => markAsRead(message.id)}
              data-component="message-alert-actions"
              data-feature="mark_alert_read_put"
              className="w-full py-3 text-white hover:bg-gray-300"
            >
              <BookOpenIcon className="h-5 w-5 inline mr-2" /> Mark As Read
            </button>
              <button
                onClick={() => {
                  markAsRead(message.id);
                  navigate('/chatdashboard', {
                    state: { highlight: message.from },
                  });
                }}
                data-component="message-alert-actions"
                data-feature="open_chatdashboard"
                className="w-full py-3 text-white hover:bg-cyan-500"
              >
                <ChatBubbleBottomCenterIcon className="h-5 w-5 inline mr-2" /> Open Chat
              </button>
            </>
          )}
          </div>
        </motion.div>
        </AnimatePresence>

        <AnimatePresence>
        {reply && (!reply.overtime || ![1, 2, 3].includes(reply.overtime)) && (
          <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.3 }}
          className="pointer-events-auto w-full max-w-md"
          >
          {loading ? (
            <div className="flex justify-center items-center p-6 bg-white/10 rounded-3xl h-32">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 100, opacity: 1 }}
              transition={{ 
              duration: 1.5,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut"
              }}
            >
              <PaperAirplaneIcon className="h-8 w-8 text-white rotate-45" />
            </motion.div>
            </div>
          ) : (
            <div className="relative rounded-3xl p-1 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-xl">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6">
              <h2 className="text-xl font-semibold text-white mb-3 drop-shadow">Send Message</h2>
              <div className="relative">
              <textarea
                onChange={(e) => setInstructionText(String(e.target.value || '').slice(0, MAX_MESSAGE_LENGTH))}
                value={instructionText}
                maxLength={MAX_MESSAGE_LENGTH}
                className="w-full h-28 resize-none rounded-xl border border-white/20 bg-white/10 p-4 pr-12 text-sm text-white placeholder-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                placeholder="Type your message..."
              />
              <button
                onClick={() => sendMessage(reply.id)}
                data-component="message-sent"
                data-feature="message-sent post"
                className="absolute bottom-3 right-3 bg-white/20 hover:bg-white/30 p-2 rounded-full shadow-md transition backdrop-blur-md"
              >
                <PaperAirplaneIcon className="h-5 w-5 text-white rotate-45" />
              </button>
              </div>
            </div>
            </div>
          )}
          </motion.div>
        )}
        </AnimatePresence>
      </div>
      </div>
    );
  }

  // Project chat message (red-500, Show & Ignore buttons)
  return (
    <div className="fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6 pointer-events-none">
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <AnimatePresence>
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, y: 50 }}
            transition={{ duration: 0.35 }}
            className="pointer-events-auto w-full max-w-md flex flex-col shadow-xl rounded-lg bg-cyan-500 bg-opacity-80 backdrop-blur-xl ring-1 ring-black/5"
          >
            <div className="flex w-full items-start p-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">From: {message.from}</p>
                <p className="mt-1 text-sm text-white">{message.content}</p>
                <p className="mt-1 text-sm text-white">Date: {message.createdAt}</p>
              </div>
            </div>
            <div className="flex w-full divide-x divide-gray-200">
              <button
                onClick={() => handleShow(message)}
                data-component="project-message-alert"
                data-time-saved="2"
                data-feature="show_project_message_delete"
                className="w-full py-3 text-white hover:bg-red-600"
              >
                <EyeIcon className="h-5 w-5 inline mr-2" /> Show
              </button>
              <button
                onClick={() => handleDelete(message)}
                data-component="project-message-alert"
                data-time-saved="2"
                data-feature="ignore_project_message_delete"
                className="w-full py-3 text-white hover:bg-gray-300"
              >
                <XCircleIcon className="h-5 w-5 inline mr-2" /> Ignore
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
